(function () {
  'use strict';

  const MOBILE_MQ = '(max-width: 989px)';
  const PANEL_MS = 520;
  const OVERFLOW_PAD = 24;
  const MIN_SUB_HEIGHT = 80;

  const SEL = Object.freeze({
    root: '.header__autify-mega-menu-nav',
    toggle: '.mobile-menu-toggle',
    drawer: '.autify-drawer',
    close: '.drawer-close',
    menu: '.nav-parents',
    dropdown: '.nav-parent--dropdown',
    navParent: '.nav-parent.has-children',
    navToggle: '.autify-nav-menu__toggle',
    navItem: '.autify-nav-menu__item--has-children',
    navOpen: '.autify-nav-menu__item--has-children.is-open',
    navActive: '.autify-nav-menu__item--has-children.is-open, .autify-nav-menu__item--has-children.is-closing',
    dimmed: '.autify-nav-menu__item--sibling-dimmed',
    titledActive: '.child-column--has-title.active',
    sub: '.autify-nav-menu__sub',
    panel: '.autify-nav-menu__sub-panel',
    row: '.autify-nav-menu__row',
    focusable: '.autify-nav-menu__toggle:focus, .autify-nav-menu__parent-link:focus, .autify-nav-menu__link:focus',
    childWrapper: '.child-wrapper',
    navMenu: '.autify-nav-menu',
    parentLink: '.nav-parent.has-children > a',
    titledTitle: '.child-column--has-title .child-menu-title',
  });

  const CLS = Object.freeze({
    open: 'is-open',
    closing: 'is-closing',
    scrollable: 'autify-nav-menu__sub--scrollable',
    scrollbarVisible: 'autify-nav-menu__sub--scrollbar-visible',
    dimmed: 'autify-nav-menu__item--sibling-dimmed',
    menuOpen: 'menu-open',
    drawerOpen: 'autify-drawer-open',
    active: 'active',
    navMenu: 'autify-nav-menu',
    instantClose: 'autify-nav-menu__item--instant-close',
  });

  const mqMobile = window.matchMedia(MOBILE_MQ);
  const mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const inited = new WeakSet();

  const isMobile = () => mqMobile.matches;

  function parts(item) {
    return {
      sub: item.querySelector(SEL.sub),
      btn: item.querySelector(SEL.navToggle),
      panel: item.querySelector(SEL.panel),
      list: item.parentElement,
    };
  }

  function waitPanel(panel, cb) {
    if (!panel) {
      cb();
      return;
    }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      panel.removeEventListener('transitionend', onEnd);
      clearTimeout(timer);
      cb();
    };
    const onEnd = (ev) => {
      if (ev.target !== panel) return;
      if (ev.propertyName && ev.propertyName !== 'grid-template-rows') return;
      finish();
    };
    const timer = setTimeout(finish, PANEL_MS);
    panel.addEventListener('transitionend', onEnd);
  }

  function resetItem(item) {
    const { sub, btn } = parts(item);
    item.classList.remove(CLS.open, CLS.closing, CLS.instantClose);
    if (sub) {
      sub.classList.remove(CLS.scrollable, CLS.scrollbarVisible);
      sub.style.removeProperty('--autify-sub-available');
      sub.setAttribute('aria-hidden', 'true');
    }
    if (btn) btn.setAttribute('aria-expanded', 'false');
  }

  function clearDimmed(scope) {
    scope.querySelectorAll(SEL.dimmed).forEach((el) => el.classList.remove(CLS.dimmed));
  }

  function setDimmed(list, current, on) {
    if (!list?.classList.contains(CLS.navMenu) || isMobile()) return;
    const children = list.children;
    for (let i = 0; i < children.length; i += 1) {
      const el = children[i];
      if (!el.classList.contains('autify-nav-menu__item')) continue;
      el.classList.toggle(CLS.dimmed, on && el !== current);
    }
  }

  function positionDropdown(header, nodes, item) {
    if (!header) return;
    const base = header.getBoundingClientRect().left;
    const targets = item ? [item] : nodes;
    for (let i = 0, n = targets.length; i < n; i += 1) {
      const node = targets[i];
      node.style.setProperty('--dropdown-left', `${Math.max(0, node.getBoundingClientRect().left - base)}px`);
    }
  }

  class AutifyMegaMenu {
    constructor(root) {
      this.root = root;
      this.scope = root.closest('.header-wrapper') || root;
      this.toggle = this.q(SEL.toggle);
      this.drawer = this.q(SEL.drawer);
      this.closeBtn = this.q(SEL.close);
      this.menu = this.q(SEL.menu);
      this.dropdowns = [...this.qa(SEL.dropdown)];
      this.parents = [];
      this.abort = null;
      this.posFrame = 0;
      this.overflowFrame = 0;
      this.resizeObserver = null;
    }

    q(selector, context = this.root) {
      return context.querySelector(selector);
    }

    qa(selector, context = this.root) {
      return context.querySelectorAll(selector);
    }

    mount() {
      if (!this.toggle || !this.drawer || !this.menu) return false;

      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.parents = [...this.qa(SEL.navParent, this.menu)];

      this.bindLayout(signal);
      this.bindDrawer(signal);
      this.bindNavToggle(signal);
      this.bindDesktop(signal);
      this.bindMobile(signal);
      this.bindViewport(signal);
      this.bindEscape(signal);

      positionDropdown(this.scope, this.dropdowns);
      return true;
    }

    bindLayout(signal) {
      const runPosition = () => {
        cancelAnimationFrame(this.posFrame);
        this.posFrame = requestAnimationFrame(() => {
          if (!isMobile()) positionDropdown(this.scope, this.dropdowns);
        });
      };

      const runOverflow = () => {
        if (isMobile()) return;
        cancelAnimationFrame(this.overflowFrame);
        this.overflowFrame = requestAnimationFrame(() => this.syncAllOpenOverflow());
      };

      const onResize = () => {
        runPosition();
        runOverflow();
      };

      window.addEventListener('resize', onResize, { signal, passive: true });
      window.addEventListener('load', runPosition, { signal, passive: true });
      document.fonts?.ready?.then(runPosition).catch(() => {});

      for (let i = 0; i < this.dropdowns.length; i += 1) {
        const item = this.dropdowns[i];
        const align = () => {
          if (!isMobile()) positionDropdown(this.scope, this.dropdowns, item);
        };
        item.addEventListener('mouseenter', align, { signal, passive: true });
        item.addEventListener('focusin', align, { signal });
      }

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(runOverflow);
        this.parents.forEach((parent) => {
          const wrap = this.q(SEL.childWrapper, parent);
          if (wrap) this.resizeObserver.observe(wrap);
        });
        signal.addEventListener('abort', () => this.resizeObserver?.disconnect());
      }
    }

    bindDrawer(signal) {
      this.setDrawer = (open) => {
        this.toggle.setAttribute('aria-expanded', String(open));
        this.toggle.classList.toggle(CLS.active, open);
        this.drawer.classList.toggle(CLS.menuOpen, open);
        this.menu.classList.toggle(CLS.menuOpen, open);
        document.documentElement.classList.toggle(CLS.drawerOpen, open);
        if (!open) this.collapseSubmenus(this.menu);
      };

      this.toggle.addEventListener('click', () => this.setDrawer(!this.drawer.classList.contains(CLS.menuOpen)), {
        signal,
      });
      this.closeBtn?.addEventListener('click', () => this.setDrawer(false), { signal });
    }

    bindNavToggle(signal) {
      this.root.addEventListener(
        'click',
        (e) => {
          const btn = e.target.closest(SEL.navToggle);
          if (!btn || !this.root.contains(btn)) return;
          e.preventDefault();
          e.stopPropagation();

          const item = btn.closest(SEL.navItem);
          if (!item || item.classList.contains(CLS.closing)) return;

          const { sub, list, panel } = parts(item);
          const opening = !item.classList.contains(CLS.open);

          this.closeOthersInList(list, opening ? item : null);

          if (!opening) {
            this.closeItemInstant(item, list);
            return;
          }

          item.classList.remove(CLS.closing);
          item.classList.add(CLS.open);
          btn.setAttribute('aria-expanded', 'true');
          if (sub) {
            sub.classList.remove(CLS.scrollable, CLS.scrollbarVisible);
            sub.style.removeProperty('--autify-sub-available');
            sub.setAttribute('aria-hidden', 'false');
          }
          setDimmed(list, item, true);

          if (!isMobile()) {
            const measure = () => this.syncSubOverflow(item, sub, panel);
            if (mqReduced.matches || !panel) {
              requestAnimationFrame(measure);
            } else {
              waitPanel(panel, measure);
            }
          }

        },
        { signal }
      );
    }

    bindDesktop(signal) {
      if (isMobile()) return;

      for (let i = 0; i < this.parents.length; i += 1) {
        const parent = this.parents[i];
        const wrap = this.q(SEL.childWrapper, parent);

        parent.addEventListener(
          'mouseenter',
          () => {
            if (isMobile()) return;
            for (let j = 0; j < this.parents.length; j += 1) {
              const other = this.parents[j];
              if (other !== parent) this.collapseSubmenus(other);
            }
          },
          { signal, passive: true }
        );

        wrap?.addEventListener(
          'mouseleave',
          (e) => {
            if (isMobile()) return;
            if (!e.relatedTarget) return;
            if (!wrap || wrap.contains(e.relatedTarget)) return;
            this.closeSubmenusAnimated(wrap);
            this.blurFocus(wrap);
          },
          { signal }
        );
      }
    }

    bindMobile(signal) {
      this.menu.addEventListener(
        'click',
        (e) => {
          if (!isMobile()) return;

          const title = e.target.closest(SEL.titledTitle);
          if (title) {
            title.closest('.child-column--has-title')?.classList.toggle(CLS.active);
            return;
          }

          const link = e.target.closest(SEL.parentLink);
          if (!link || !this.menu.contains(link)) return;
          e.preventDefault();

          const parent = link.parentElement;
          const wasActive = parent?.classList.contains(CLS.active);

          const actives = this.qa(`${SEL.navParent}.${CLS.active}`, this.menu);
          for (let i = 0; i < actives.length; i += 1) {
            const el = actives[i];
            el.classList.remove(CLS.active);
            this.collapseTitles(el);
            this.collapseSubmenus(el);
          }

          if (!wasActive && parent) parent.classList.add(CLS.active);
        },
        { signal }
      );
    }

    bindViewport(signal) {
      const onChange = () => {
        if (!isMobile()) this.setDrawer(false);
        this.qa(`${SEL.navParent}.${CLS.active}`, this.menu).forEach((el) => el.classList.remove(CLS.active));
        this.collapseTitles(this.menu);
        this.collapseSubmenus(this.menu);
        cancelAnimationFrame(this.posFrame);
        this.posFrame = requestAnimationFrame(() => {
          if (!isMobile()) positionDropdown(this.scope, this.dropdowns);
        });
      };

      mqMobile.addEventListener?.('change', onChange, { signal }) ?? mqMobile.addListener(onChange);
    }

    bindEscape(signal) {
      document.addEventListener(
        'keydown',
        (e) => {
          if (e.key !== 'Escape' || !this.root.isConnected) return;
          if (this.drawer.classList.contains(CLS.menuOpen)) {
            this.setDrawer(false);
            this.toggle.focus();
            return;
          }
          if (!isMobile() && this.q(SEL.navOpen)) {
            this.closeSubmenusAnimated(this.root);
            this.blurFocus(this.root);
          }
        },
        { signal }
      );
    }

    collapseTitles(scope) {
      scope.querySelectorAll(SEL.titledActive).forEach((el) => el.classList.remove(CLS.active));
    }

    collapseSubmenus(scope) {
      scope.querySelectorAll(SEL.navActive).forEach(resetItem);
      clearDimmed(scope);
    }

    blurFocus(scope) {
      scope.querySelectorAll(SEL.focusable).forEach((el) => el.blur());
    }

    closeOthersInList(list, keep) {
      if (!list?.classList.contains(CLS.navMenu)) return;
      const open = list.querySelectorAll(':scope > li.autify-nav-menu__item--has-children.is-open');
      for (let i = 0; i < open.length; i += 1) {
        if (open[i] !== keep) resetItem(open[i]);
      }
    }

    closeItemInstant(item, list) {
      item.classList.add(CLS.instantClose);
      resetItem(item);
      setDimmed(list, null, false);
      requestAnimationFrame(() => item.classList.remove(CLS.instantClose));
    }

    closeSubmenusAnimated(scope) {
      const items = scope.querySelectorAll(SEL.navOpen);
      if (!items.length) return;

      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        const { sub, btn, list, panel } = parts(item);

        if (sub) {
          sub.classList.remove(CLS.scrollable, CLS.scrollbarVisible);
          sub.style.removeProperty('--autify-sub-available');
          sub.setAttribute('aria-hidden', 'true');
        }
        if (btn) {
          btn.setAttribute('aria-expanded', 'false');
          btn.blur();
        }

        if (isMobile()) {
          item.classList.remove(CLS.open, CLS.closing);
          continue;
        }

        item.classList.remove(CLS.open);
        item.classList.add(CLS.closing);
        this.afterClose(item, list, panel);
      }
    }

    afterClose(item, list, panel) {
      const finish = () => {
        item.classList.remove(CLS.closing);
        setDimmed(list, null, false);
      };
      if (!panel || isMobile()) {
        finish();
        return;
      }
      waitPanel(panel, finish);
    }

    syncAllOpenOverflow() {
      if (isMobile()) return;
      this.qa(SEL.navOpen).forEach((item) => {
        const { sub, panel } = parts(item);
        if (sub) this.syncSubOverflow(item, sub, panel);
      });
    }

    syncSubOverflow(item, sub, panel) {
      if (!item?.classList.contains(CLS.open) || !sub) return;

      sub.classList.remove(CLS.scrollable, CLS.scrollbarVisible);

      const wrap = item.closest(SEL.childWrapper);
      const row = item.querySelector(SEL.row);
      if (wrap && row) {
        const available = Math.floor(wrap.getBoundingClientRect().bottom - row.getBoundingClientRect().bottom - OVERFLOW_PAD);
        if (available > MIN_SUB_HEIGHT) {
          sub.style.setProperty('--autify-sub-available', `${available}px`);
        } else {
          sub.style.removeProperty('--autify-sub-available');
        }
      }

      void sub.offsetHeight;
      const overflows = sub.scrollHeight > sub.clientHeight + 2;
      if (!overflows) return;

      sub.classList.add(CLS.scrollable, CLS.scrollbarVisible);
    }

    destroy() {
      this.abort?.abort();
      this.resizeObserver?.disconnect();
    }
  }

  function init(root) {
    if (root._autifyMenu) {
      root._autifyMenu.destroy();
      inited.delete(root);
    } else if (inited.has(root)) {
      return;
    }
    const instance = new AutifyMegaMenu(root);
    if (!instance.mount()) return;
    inited.add(root);
    root._autifyMenu = instance;
  }

  function boot(event) {
    const roots = event?.target
      ? event.target.querySelectorAll(SEL.root)
      : document.querySelectorAll(SEL.root);
    roots.forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
